/**
 * #10766 (H4, EPIC #10762) — the Night Flight header search. The button opens a search box that
 * queries the live site search (GET /api/search, the proud-block-16ed AI Search crawler of www).
 * Each result links to its page; an empty result shows the free consult link.
 *
 * Keyboard: "/" or Cmd/Ctrl+K opens it, Escape closes it, the arrow keys move through the results
 * and Enter opens the active one. On a phone the box fills the width under a safe top gap.
 */
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Search, X } from 'lucide-react';
import { CONSULT_HREF, COPY, DEBOUNCE_MS, parseResults, searchUrl, stepActive, type HeaderSearchResult } from './headerSearchModel';

type State = { kind: 'idle' } | { kind: 'loading' } | { kind: 'done'; results: HeaderSearchResult[] } | { kind: 'failed' };

export function HeaderSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const listId = useId();

  const close = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  // "/" or Cmd/Ctrl+K opens the box from anywhere on the page, except while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Debounced search; a newer query aborts the older request.
  useEffect(() => {
    const url = searchUrl(query);
    setActive(-1);
    if (!open || !url) {
      setState({ kind: 'idle' });
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setState({ kind: 'loading' });
      try {
        const res = await fetch(url, { signal: controller.signal, headers: { accept: 'application/json' } });
        if (!res.ok) throw new Error(`search ${res.status}`);
        setState({ kind: 'done', results: parseResults(await res.json()) });
      } catch {
        if (!controller.signal.aborted) setState({ kind: 'failed' });
      }
    }, DEBOUNCE_MS);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [open, query]);

  const results = state.kind === 'done' ? state.results : [];

  function onInputKey(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => stepActive(a, e.key as 'ArrowDown' | 'ArrowUp', results.length));
    } else if (e.key === 'Enter' && active >= 0 && results[active]) {
      e.preventDefault();
      window.location.assign(results[active].url);
    }
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(true)}
        className="p-2.5 rounded-lg text-foreground/80 hover:text-primary"
        aria-label={COPY.open}
        aria-haspopup="dialog"
        title={`${COPY.open} (/)`}
      >
        <Search className="w-5 h-5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 px-4 pt-[max(4rem,env(safe-area-inset-top))]" onClick={close}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="w-full max-w-xl rounded-2xl border border-border bg-background shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id={titleId} className="sr-only">
              {COPY.label}
            </h2>
            <div className="flex items-center gap-2 border-b border-border px-4">
              <Search className="h-5 w-5 shrink-0 text-foreground/50" aria-hidden />
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onInputKey}
                placeholder={COPY.placeholder}
                aria-label={COPY.label}
                role="combobox"
                aria-expanded={results.length > 0}
                aria-controls={listId}
                aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
                autoComplete="off"
                enterKeyHint="search"
                className="h-14 w-full bg-transparent text-base text-foreground outline-none placeholder:text-foreground/40"
              />
              <button type="button" onClick={close} aria-label={COPY.close} className="rounded p-1 text-foreground/60 hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-2" aria-live="polite">
              {state.kind === 'idle' && <p className="px-3 py-4 text-sm text-foreground/60">{COPY.hint}</p>}
              {state.kind === 'loading' && <p className="px-3 py-4 text-sm text-foreground/60">{COPY.searching}</p>}
              {state.kind === 'failed' && (
                <p className="px-3 py-4 text-sm text-foreground/70">
                  {COPY.failed}{' '}
                  <a href={CONSULT_HREF} className="font-semibold text-primary hover:underline">
                    {COPY.consult}
                  </a>
                </p>
              )}
              {state.kind === 'done' && results.length === 0 && (
                <div className="px-3 py-4 text-sm text-foreground/70">
                  <p>{COPY.empty}</p>
                  <a href={CONSULT_HREF} className="mt-3 inline-flex rounded-xl card-primary-cta px-4 py-2 font-semibold">
                    {COPY.consult}
                  </a>
                </div>
              )}
              {results.length > 0 && (
                <ul id={listId} role="listbox" aria-label={COPY.label} className="space-y-1">
                  {results.map((r, i) => (
                    <li key={r.url} id={`${listId}-${i}`} role="option" aria-selected={i === active}>
                      <a
                        href={r.url}
                        onMouseEnter={() => setActive(i)}
                        className={`block rounded-xl px-3 py-2.5 ${i === active ? 'bg-primary/10' : 'hover:bg-primary/5'}`}
                      >
                        <span className="block text-sm font-semibold text-foreground">{r.title}</span>
                        {r.description && <span className="mt-0.5 block text-xs text-foreground/60 line-clamp-2">{r.description}</span>}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
